import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BackButton } from '@/components/ui/back-button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    userApi,
    organizationApi,
    authorizationApi,
    permissionsApi,
    authApi,
} from '@/lib/pillarApi';
import {
    getOrganizationCapacityDisplayName,
    getOrganizationCapacityFilter,
    getOrganizationCapacityLabel,
    organizationHasCapacity,
} from '@/lib/organization-capacity';
import { AllOrganizationalRoles } from '@pillar-portal/database/dist/frontend';
import { runInBackground } from '@/lib/utils';
import { getActiveRoleAssignmentOperationalResponse } from '@/lib/active-role-assignment';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import { useAuthStore } from '@/store/auth';
import { Checkbox } from '@/components/ui/checkbox';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import {
    type OrganizationCapacityKey,
    type GetOrganizationResponse,
    GetOrganizationResponseSchema,
    type OrganizationOmnisearchListItem,
    type PermissionsBundlePublic,
    type PolicyPermissionBundlesSearchPageResponse,
    type ProvisionedUser,
    type Role,
    RoleSchema,
    normalizeUserEmailInputForSubmission,
    UserEmailIdentitySchema,
} from '@pillar-portal/api-types';

const parseRoleValue = (value: string): Role | null => {
    const parseResult = RoleSchema.safeParse(value);
    if (!parseResult.success) {
        return null;
    }
    return parseResult.data;
};

interface CreateScopedOrganizationalUserPageProps {
    organizationCapacityKey: OrganizationCapacityKey;
    pageTitle: string;
    backHref: string;
    backTooltip: string;
    createButtonLabel: string;
    invalidateQueryKeys: ReadonlyArray<ReadonlyArray<unknown>>;
}

type SelectedPolicyBundle = Pick<PermissionsBundlePublic, 'permissions_bundle_uuid' | 'name'>;

export function ProvisionedUserResultCard({
    provisionedUser,
    title,
    onViewUser,
    onCreateAnother,
}: {
    provisionedUser: ProvisionedUser;
    title: string;
    onViewUser: () => void;
    onCreateAnother: () => void;
}) {
    const deliveryDescription: string =
        provisionedUser.delivery.delivery_outcome === 'not_attempted'
            ? 'Invitation delivery was not attempted.'
            : provisionedUser.delivery.delivery_outcome === 'provider_accepted'
              ? `The email provider accepted the invitation at ${provisionedUser.delivery.attempted_at}.`
              : provisionedUser.delivery.delivery_outcome === 'unconfirmed'
                ? `Delivery unconfirmed at ${provisionedUser.delivery.attempted_at}.`
                : `Invitation delivery failed at ${provisionedUser.delivery.attempted_at}.`;
    return (
        <Card className="max-w-2xl">
            <CardHeader>
                <CardTitle>{title}</CardTitle>
                <CardDescription>
                    The user record has been created with UUID {provisionedUser.user_uuid}.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div>
                    <div className="text-sm font-medium">Account state</div>
                    <div className="text-sm text-muted-foreground">
                        {provisionedUser.activation_state === 'pending_first_login'
                            ? 'Pending first login'
                            : 'Active — verification bypassed'}
                    </div>
                </div>
                <div>
                    <div className="text-sm font-medium">Delivery</div>
                    <div className="text-sm text-muted-foreground">{deliveryDescription}</div>
                </div>
                {provisionedUser.activation_state === 'pending_first_login' ? (
                    <div>
                        <div className="text-sm font-medium">Invitation expires</div>
                        <div className="text-sm text-muted-foreground">
                            {provisionedUser.first_login_expires_at}
                        </div>
                    </div>
                ) : null}
                <div className="flex gap-2">
                    <Button onClick={onViewUser}>View User</Button>
                    <Button variant="outline" onClick={onCreateAnother}>
                        Create Another User
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

function toLockedOrganizationSelection(
    organization: GetOrganizationResponse
): OrganizationOmnisearchListItem {
    if (organization.organization_uuid === undefined) {
        throw new Error('Locked organization is missing organization_uuid');
    }
    return {
        ...organization,
        organization_uuid: organization.organization_uuid,
        parent_organization_uuid: organization.parent_organization_uuid ?? null,
    };
}

export function CreateScopedOrganizationalUserPage(props: CreateScopedOrganizationalUserPageProps) {
    const log = createComponentLogger('CreateScopedOrganizationalUserPage');
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const queryClient = useQueryClient();
    const bumpUserListsVersion: () => void = useDataInvalidationStore(
        (state) => state.bumpUserListsVersion
    );
    const [searchParams] = useSearchParams();

    const organizationUuidFromUrl = searchParams.get('organization_uuid');
    const lockedOrgUuidParseResult = z.string().uuid().safeParse(organizationUuidFromUrl);
    const lockedOrgUuid: string | null = lockedOrgUuidParseResult.success
        ? lockedOrgUuidParseResult.data
        : null;

    const [primaryEmail, setPrimaryEmail] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [role, setRole] = useState<Role | ''>('');
    const [organizationUuid, setOrganizationUuid] = useState<string>(lockedOrgUuid ?? '');
    const [selectedOrganization, setSelectedOrganization] =
        useState<OrganizationOmnisearchListItem | null>(null);
    const [selectedPolicyBundles, setSelectedPolicyBundles] = useState<SelectedPolicyBundle[]>([]);
    const [policySelectorResetToken, setPolicySelectorResetToken] = useState<number>(0);
    const [skipVerification, setSkipVerification] = useState<boolean>(false);
    const [provisionedUser, setProvisionedUser] = useState<ProvisionedUser | null>(null);
    const currentUser = useAuthStore((state) => state.user);
    const activeRoleAssignmentQuery = useQuery({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await authApi.getActiveRoleAssignment(),
        enabled: currentUser !== null,
        staleTime: 30000,
    });
    const activeRoleAssignmentOperationalResponse = getActiveRoleAssignmentOperationalResponse(
        activeRoleAssignmentQuery.data
    );
    const currentUserIsInternalAdminOrHigher: boolean =
        activeRoleAssignmentOperationalResponse?.role === 'SUPERADMIN' ||
        activeRoleAssignmentOperationalResponse?.role === 'INTERNAL_ADMIN';

    const availableRolesForKind: ReadonlyArray<Role> = useMemo(() => {
        return AllOrganizationalRoles;
    }, []);

    const { data: lockedOrgFromApi } = useQuery<GetOrganizationResponse>({
        queryKey: ['organization', lockedOrgUuid],
        queryFn: async () => {
            if (lockedOrgUuid === null) {
                throw new Error('lockedOrgUuid is required');
            }
            const response = await organizationApi.getOrganization(lockedOrgUuid);
            return GetOrganizationResponseSchema.parse(response);
        },
        enabled: lockedOrgUuid !== null,
    });

    const lockedOrg: GetOrganizationResponse | null = lockedOrgFromApi ?? null;
    const effectiveSelectedOrganization: OrganizationOmnisearchListItem | null =
        selectedOrganization ??
        (lockedOrg === null ? null : toLockedOrganizationSelection(lockedOrg));

    const { data: canCreateInUrlOrg } = useQuery<boolean>({
        queryKey: ['authorization', 'can_create_user', lockedOrg?.organization_uuid],
        queryFn: async () => {
            if (lockedOrg === null) {
                return false;
            }
            const orgUuidParseResult = z.string().uuid().safeParse(lockedOrg.organization_uuid);
            if (!orgUuidParseResult.success) {
                throw new Error('Locked organization is missing organization_uuid');
            }
            const response = await authorizationApi.canCreateUser(orgUuidParseResult.data);
            return response.can_create;
        },
        enabled: lockedOrg !== null,
    });

    const handleOrganizationSelected = useCallback(
        (organization: OrganizationOmnisearchListItem | null) => {
            if (organization === null) {
                setOrganizationUuid('');
                setSelectedOrganization(null);
                setSelectedPolicyBundles([]);
                setPolicySelectorResetToken((currentValue) => currentValue + 1);
                setRole('');
                return;
            }

            if (!organizationHasCapacity(organization, props.organizationCapacityKey)) {
                throw new Error(
                    `Invalid organization capacity: expected ${props.organizationCapacityKey}, got ${getOrganizationCapacityLabel(organization)}`
                );
            }

            const nextOrganizationUuid: string = organization.organization_uuid;
            if (organizationUuid !== nextOrganizationUuid) {
                setSelectedPolicyBundles([]);
                setPolicySelectorResetToken((currentValue) => currentValue + 1);
            }
            setOrganizationUuid(nextOrganizationUuid);
            setSelectedOrganization(organization);

            if (availableRolesForKind.length === 1) {
                const onlyRole: Role | undefined = availableRolesForKind[0];
                if (onlyRole !== undefined) {
                    setRole(onlyRole);
                }
                return;
            }

            if (role !== '' && availableRolesForKind.includes(role)) {
                return;
            }
            setRole('');
        },
        [availableRolesForKind, organizationUuid, props.organizationCapacityKey, role]
    );

    useEffect(() => {
        if (lockedOrg !== null) {
            handleOrganizationSelected(toLockedOrganizationSelection(lockedOrg));
        }
    }, [lockedOrg, handleOrganizationSelected]);

    const createUserMutation = useMutation({
        mutationFn: userApi.provisionOrganizationalUser,
        onSuccess: (data) => {
            bumpUserListsVersion();
            for (const queryKey of props.invalidateQueryKeys) {
                runInBackground(
                    queryClient.invalidateQueries({ queryKey: [...queryKey] }),
                    (err) => {
                        log.error('Error invalidating queries:', err);
                    }
                );
            }
            setProvisionedUser(data);
        },
    });

    const updatePrimaryEmail = (emailAddress: string): void => {
        setPrimaryEmail(normalizeUserEmailInputForSubmission(emailAddress));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!primaryEmail || !role || !organizationUuid) {
            return;
        }
        const normalizedPrimaryEmail: string = normalizeUserEmailInputForSubmission(primaryEmail);
        const isValidEmailIdentity: boolean =
            UserEmailIdentitySchema.safeParse(normalizedPrimaryEmail).success;
        if (!isValidEmailIdentity) {
            return;
        }
        setPrimaryEmail(normalizedPrimaryEmail);

        const parsedRole: Role | null = parseRoleValue(role);
        if (parsedRole === null) {
            throw new Error(`Invalid role: ${role}`);
        }
        if (!availableRolesForKind.includes(parsedRole)) {
            throw new Error(`Role ${parsedRole} is not allowed on this page`);
        }
        if (effectiveSelectedOrganization === null) {
            throw new Error('Organization is required');
        }
        const organizationUuidParseResult = z
            .string()
            .uuid()
            .safeParse(effectiveSelectedOrganization.organization_uuid);
        if (!organizationUuidParseResult.success) {
            throw new Error('Selected organization is missing organization_uuid');
        }
        if (
            !organizationHasCapacity(effectiveSelectedOrganization, props.organizationCapacityKey)
        ) {
            throw new Error(
                `Selected organization capacity mismatch: expected ${props.organizationCapacityKey}, got ${getOrganizationCapacityLabel(effectiveSelectedOrganization)}`
            );
        }

        createUserMutation.mutate({
            primary_email: normalizedPrimaryEmail,
            emails: [normalizedPrimaryEmail],
            role: parsedRole,
            organization_uuid: organizationUuidParseResult.data,
            first_name: firstName.trim() || null,
            last_name: lastName.trim() || null,
            skip_verification: skipVerification,
            policies: selectedPolicyBundles.map((bundle) => bundle.permissions_bundle_uuid),
        });
    };

    const policySelectorOrganizationUuid: string =
        effectiveSelectedOrganization?.organization_uuid ?? 'none';

    const pageTitle: string =
        lockedOrg !== null ? `${props.pageTitle} for ${lockedOrg.name}` : props.pageTitle;

    const showLockedOrgTypeMismatch: boolean =
        lockedOrg !== null && !organizationHasCapacity(lockedOrg, props.organizationCapacityKey);
    const hasPrimaryEmailValue: boolean = primaryEmail.trim().length > 0;
    const hasOrganizationSelection: boolean =
        organizationUuid.length > 0 && effectiveSelectedOrganization !== null;
    const hasRoleSelection: boolean = role !== '';
    const canSubmitCreateUser: boolean =
        !createUserMutation.isPending &&
        hasPrimaryEmailValue &&
        hasOrganizationSelection &&
        hasRoleSelection;

    if (lockedOrgUuid !== null && canCreateInUrlOrg === false) {
        return (
            <div>
                <BackButton href={props.backHref} tooltip={props.backTooltip} />
                <h1 className="text-3xl font-bold mb-8">{props.pageTitle}</h1>
                <Alert variant="destructive">
                    <AlertDescription>
                        You do not have permission to create users in organization {lockedOrgUuid}.
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    if (showLockedOrgTypeMismatch) {
        return (
            <div>
                <BackButton href={props.backHref} tooltip={props.backTooltip} />
                <h1 className="text-3xl font-bold mb-8">{props.pageTitle}</h1>
                <Alert variant="destructive">
                    <AlertDescription>
                        This page only supports{' '}
                        {getOrganizationCapacityDisplayName(props.organizationCapacityKey)}{' '}
                        organizations, but organization {lockedOrg?.organization_uuid ?? 'unknown'}{' '}
                        has capacities{' '}
                        {lockedOrg !== null ? getOrganizationCapacityLabel(lockedOrg) : 'unknown'}.
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    if (provisionedUser !== null) {
        return (
            <div>
                <BackButton href={props.backHref} tooltip={props.backTooltip} />
                <ProvisionedUserResultCard
                    provisionedUser={provisionedUser}
                    title="User Created Successfully"
                    onViewUser={() =>
                        breadcrumbNavigate({
                            source: 'terminal',
                            target: {
                                url: `/users/${provisionedUser.user_uuid}`,
                                title: 'User Details',
                            },
                        })
                    }
                    onCreateAnother={() => {
                        setProvisionedUser(null);
                        setPrimaryEmail('');
                        setFirstName('');
                        setLastName('');
                        setRole('');
                        setSelectedPolicyBundles([]);
                        setPolicySelectorResetToken((currentValue) => currentValue + 1);
                    }}
                />
            </div>
        );
    }

    return (
        <div>
            <BackButton href={props.backHref} tooltip={props.backTooltip} />
            <div className="flex items-center gap-4 mb-8">
                <h1 className="text-3xl font-bold">{pageTitle}</h1>
            </div>

            <Card className="max-w-2xl">
                <CardHeader>
                    <CardTitle>Invite New User</CardTitle>
                    <CardDescription>
                        Create a new organizational user account. Pending users receive an
                        invitation to complete their first login.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form
                        id="create-organizational-user-form"
                        onSubmit={handleSubmit}
                        className="space-y-4"
                    >
                        <div className="space-y-2">
                            <Label htmlFor="primaryEmail">Primary Email</Label>
                            <Input
                                id="primaryEmail"
                                type="email"
                                value={primaryEmail}
                                onChange={(e) => updatePrimaryEmail(e.currentTarget.value)}
                                onBlur={(e) => updatePrimaryEmail(e.currentTarget.value)}
                                required
                                placeholder="user@example.com"
                                disabled={createUserMutation.isPending}
                            />
                        </div>
                        {currentUserIsInternalAdminOrHigher && (
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="skipVerification"
                                    checked={skipVerification}
                                    onCheckedChange={(checked) =>
                                        setSkipVerification(checked === true)
                                    }
                                />
                                <Label htmlFor="skipVerification">
                                    Skip verification (internal admin or higher)
                                </Label>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="firstName">First Name</Label>
                                <Input
                                    id="firstName"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    disabled={createUserMutation.isPending}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="lastName">Last Name</Label>
                                <Input
                                    id="lastName"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    disabled={createUserMutation.isPending}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="organization">Organization</Label>
                            {lockedOrgUuid !== null ? (
                                <Input
                                    id="organization"
                                    value={lockedOrg?.name ?? 'Loading organization...'}
                                    disabled
                                />
                            ) : (
                                <OmnisearchSelect<OrganizationOmnisearchListItem, string>
                                    label={`Search ${getOrganizationCapacityDisplayName(props.organizationCapacityKey)} organizations`}
                                    placeholder="Type to search..."
                                    pageSize={25}
                                    disabled={createUserMutation.isPending}
                                    selectedItemId={
                                        organizationUuid.length > 0 ? organizationUuid : null
                                    }
                                    onClearSelection={() => {
                                        setOrganizationUuid('');
                                        setSelectedOrganization(null);
                                        setSelectedPolicyBundles([]);
                                        setPolicySelectorResetToken(
                                            (currentValue) => currentValue + 1
                                        );
                                        setRole('');
                                    }}
                                    fetchPage={async (params) => {
                                        const response = await organizationApi.searchOrganizations({
                                            ...(params.terms !== undefined
                                                ? { terms: [...params.terms] }
                                                : {}),
                                            ...getOrganizationCapacityFilter(
                                                props.organizationCapacityKey
                                            ),
                                            limit: params.limit,
                                            cursor: params.cursor,
                                        });
                                        return {
                                            items: response.organizations,
                                            pagination: response.pagination,
                                        };
                                    }}
                                    getItemId={(item) => item.organization_uuid}
                                    getItemLabel={(item) =>
                                        `${item.name} (${getOrganizationCapacityLabel(item)})`
                                    }
                                    getItemFilterText={(item) =>
                                        `${item.name} ${item.organization_uuid}`
                                    }
                                    onSelect={(item) => handleOrganizationSelected(item)}
                                />
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label>Policies</Label>
                            <OmnisearchSelect<PermissionsBundlePublic, string>
                                label="Add Policy"
                                placeholder="Search policies..."
                                pageSize={25}
                                disabled={
                                    createUserMutation.isPending ||
                                    effectiveSelectedOrganization === null
                                }
                                resetToken={`org=${policySelectorOrganizationUuid};reset=${policySelectorResetToken}`}
                                fetchPage={async (params) => {
                                    if (effectiveSelectedOrganization === null) {
                                        throw new Error('Organization is required');
                                    }
                                    const response: PolicyPermissionBundlesSearchPageResponse =
                                        await permissionsApi.searchPolicyPermissionBundlesForOrganization(
                                            effectiveSelectedOrganization.organization_uuid,
                                            {
                                                ...(params.terms !== undefined
                                                    ? { terms: [...params.terms] }
                                                    : {}),
                                                limit: params.limit,
                                                cursor: params.cursor,
                                            }
                                        );
                                    return {
                                        items: response.bundles,
                                        pagination: response.pagination,
                                    };
                                }}
                                getItemId={(bundle) => bundle.permissions_bundle_uuid}
                                getItemLabel={(bundle) => bundle.name}
                                getItemFilterText={(bundle) =>
                                    `${bundle.name} ${bundle.description ?? ''} ${bundle.permissions_bundle_uuid}`
                                }
                                selectedItemId={null}
                                onSelect={(bundle) => {
                                    setSelectedPolicyBundles((currentBundles) => {
                                        const isAlreadySelected: boolean = currentBundles.some(
                                            (currentBundle) =>
                                                currentBundle.permissions_bundle_uuid ===
                                                bundle.permissions_bundle_uuid
                                        );
                                        if (isAlreadySelected) {
                                            return currentBundles;
                                        }
                                        return [
                                            ...currentBundles,
                                            {
                                                permissions_bundle_uuid:
                                                    bundle.permissions_bundle_uuid,
                                                name: bundle.name,
                                            },
                                        ];
                                    });
                                    setPolicySelectorResetToken((currentValue) => currentValue + 1);
                                }}
                            />
                            {selectedPolicyBundles.length === 0 ? (
                                <div className="text-sm text-muted-foreground">
                                    No policies selected.
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {selectedPolicyBundles.map((bundle) => (
                                        <div
                                            key={bundle.permissions_bundle_uuid}
                                            className="flex items-center justify-between rounded border p-2 text-sm"
                                        >
                                            <span>{bundle.name}</span>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    setSelectedPolicyBundles((currentBundles) =>
                                                        currentBundles.filter(
                                                            (currentBundle) =>
                                                                currentBundle.permissions_bundle_uuid !==
                                                                bundle.permissions_bundle_uuid
                                                        )
                                                    );
                                                }}
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="role">Role</Label>
                            <Select
                                value={role}
                                onValueChange={(value) => {
                                    const parsed: Role | null = parseRoleValue(value);
                                    if (parsed === null) {
                                        setRole('');
                                        return;
                                    }
                                    if (!availableRolesForKind.includes(parsed)) {
                                        throw new Error(
                                            `Role ${parsed} is not allowed on this page`
                                        );
                                    }
                                    setRole(parsed);
                                }}
                                disabled={
                                    createUserMutation.isPending ||
                                    availableRolesForKind.length === 1
                                }
                            >
                                <SelectTrigger id="role">
                                    <SelectValue placeholder="Select a role" />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableRolesForKind.map((r) => (
                                        <SelectItem
                                            key={r}
                                            id={`create-organizational-user-role-${r}`}
                                            value={r}
                                        >
                                            {r}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {createUserMutation.error && (
                            <Alert id="create-organizational-user-error" variant="destructive">
                                <AlertDescription>
                                    {createUserMutation.error instanceof Error
                                        ? createUserMutation.error.message
                                        : 'Failed to create user'}
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="flex gap-2">
                            <Button
                                id="create-organizational-user-submit"
                                type="submit"
                                disabled={!canSubmitCreateUser}
                            >
                                {createUserMutation.isPending
                                    ? 'Creating...'
                                    : props.createButtonLabel}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() =>
                                    breadcrumbNavigate({
                                        source: 'terminal',
                                        target: {
                                            url: props.backHref,
                                        },
                                    })
                                }
                                disabled={createUserMutation.isPending}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
